<script lang="ts">
import { type Instrument, InstrumentState } from '~/models/instruments';
</script>

<script setup lang="ts">
import IntroductionCard from '~/components/investments-offer/IntroductionCard.vue';
import IntroductionDescription from '~/components/investments-offer/IntroductionDescription.vue';
import Gallery from '~/components/investments-offer/Gallery.vue';
import Description from '~/components/investments-offer/Description.vue';
import History from '~/components/investments-offer/History.vue';
import Timeline from '~/components/investments-offer/Timeline.vue';
import Specification from '~/components/investments-offer/Specification.vue';
import InvestmentCertificateStructure from '~/components/investments-offer/InvestmentCertificateStructure.vue';

definePageMeta({
  layout: 'light',
});

const { differenceInSeconds, format } = useDateFns();
const { t, locale } = useI18n();
const localePath = useLocalePath();
const router = useRouter();
const route = useRoute();
const instrumentsStore = useInstrumentsStore();
const portfoliosStore = usePortfoliosStore();
const { instrumentsCache } = storeToRefs(instrumentsStore);

const loaded = ref(false);
const instrument = ref<Instrument>({} as Instrument);

watch(
  route,
  async () => {
    const slug = route.params.slug as string;
    const language = locale.value as 'cs' | 'en';
    let localInstrument: Instrument | null | undefined = instrumentsCache.value[language].data.find(
      (instrument) => instrument.Slug === decodeURIComponent(slug.toLowerCase()),
    );

    if (localInstrument) {
      const differenceFromStart = differenceInSeconds(
        new Date(),
        new Date(localInstrument.IssueDate),
      );
      const differenceFromEnd = differenceInSeconds(
        new Date(),
        new Date(format(new Date(localInstrument.EndOfSubscription), "yyyy-MM-dd'T'23:59:59'Z'")),
      );
      if (
        localInstrument.State === InstrumentState.InPreparation &&
        differenceFromStart > 0 &&
        differenceFromEnd < 0
      ) {
        localInstrument = null;
      }
    }

    localInstrument ??= await instrumentsStore.getInstrument({
      slug: slug.toLowerCase(),
      force: true,
    });

    if (!localInstrument || localInstrument.State === InstrumentState.Hidden) {
      await router.push(localePath('investments-offer'));

      return;
    }

    instrument.value = localInstrument;
    loaded.value = true;
  },
  { immediate: true },
);

useCustomSeoMeta({
  title: computed(() =>
    t('investment-offer-detail.meta.title', { instrument: instrument.value.Name }),
  ),
  description: t('investment-offer-detail.meta.content'),
});

onMounted(() => {
  portfoliosStore.getDashboard();
});
</script>

<template>
  <ClientOnly v-if="loaded">
    <IntroductionCard :instrument="instrument" />

    <IntroductionDescription :instrument="instrument" />

    <Gallery :instrument="instrument" />

    <Description :instrument="instrument" />

    <History :instrument="instrument" />

    <Timeline :instrument="instrument" />

    <Specification :instrument="instrument" />

    <InvestmentCertificateStructure :instrument="instrument" />
  </ClientOnly>
</template>
